import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApplications } from '../contexts/ApplicationContext';
import { useCourses } from '../contexts/CourseContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CalendarIcon, DocumentTextIcon, CreditCardIcon, UserIcon } from '@heroicons/react/24/outline';

const MyPage: React.FC = () => {
  const { user, isStudent } = useAuth();
  const { getApplicationsByStudent, cancelApplication } = useApplications();
  const { getCourse } = useCourses();
  const [activeTab, setActiveTab] = useState<'profile' | 'applications'>('applications');

  if (!user || !isStudent) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <p className="text-gray-500">학생으로 로그인해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  const applications = getApplicationsByStudent(user.id);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '접수완료';
      case 'approved': return '결제대기';
      case 'rejected': return '신청취소';
      case 'waiting': return '대기중';
      case 'cancelled': return '신청취소';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCancelApplication = (applicationId: string) => {
    if (window.confirm('수강신청을 취소하시겠습니까?')) {
      cancelApplication(applicationId);
      alert('수강신청이 취소되었습니다.');
    }
  };

  return (
    <div>
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">마이페이지</h1>
          <p className="text-gray-600">{user.name}님의 수강신청 내역을 확인하세요.</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('applications')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'applications'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                수강신청 내역
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                회원 정보
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'applications' && (
          <div>
            <div className="bg-white rounded-lg shadow">
              {applications.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">신청 내역이 없습니다</h3>
                  <p className="mt-2 text-gray-500">아직 수강신청한 강의가 없습니다.</p>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="space-y-6">
                      {applications.map((application) => {
                        const course = getCourse(application.courseId);
                        if (!course) return null;

                        return (
                          <div key={application.id} className="border border-gray-200 rounded-lg p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{course.courseName}</h3>
                                <p className="text-gray-600">강사: {course.instructor}</p>
                              </div>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                                {getStatusLabel(application.status)}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div className="flex items-center text-sm text-gray-600">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                신청일: {new Date(application.applicationDate).toLocaleDateString('ko-KR')}
                              </div>
                              <div className="flex items-center text-sm text-gray-600">
                                <CreditCardIcon className="h-4 w-4 mr-2" />
                                금액: {application.paymentAmount.toLocaleString()}원
                              </div>
                              <div className="flex items-center text-sm text-gray-600">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                교육기간: {new Date(course.courseStartDate).toLocaleDateString('ko-KR')} ~ {new Date(course.courseEndDate).toLocaleDateString('ko-KR')}
                              </div>
                              <div className="flex items-center text-sm text-gray-600">
                                <DocumentTextIcon className="h-4 w-4 mr-2" />
                                교육시간: {course.courseTime} ({course.courseDays.join(', ')})
                              </div>
                            </div>

                            {application.status === 'pending' && (
                              <div className="flex justify-end">
                                <button
                                  onClick={() => handleCancelApplication(application.id)}
                                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                                >
                                  신청 취소
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 py-5 sm:p-6">
                <div className="text-center py-12">
                  <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">회원 정보</h3>
                  <p className="mt-2 text-gray-500">회원 정보 기능은 준비 중입니다.</p>
                  <div className="mt-6 space-y-2">
                    <p className="text-sm text-gray-600">이름: {user.name}</p>
                    <p className="text-sm text-gray-600">이메일: {user.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default MyPage;